import { Level } from '../types/level';
import { LevelDifficultyPoint } from '../types/levelDifficultyPoint';
import { boolHttp, http, objectHttp } from './http';
import {
  DeleteLevelUrl,
  FullLevelUrl,
  GetDifficultyPointsUrl,
  RecalculateDifficultyUrl,
  ReorderLevelsByDifficultyUrl,
  RebuildLevelUrl,
  GetLevelUrl,
  GetLevelsUrl,
  SaveLevelUrl,
} from './urls';

interface GetFullLevelRequest {
  lang: string;
  keyword: string;
  number: number;
  min: number;
  max: number;
}

export interface GetFullLevelResponse {
  levels: Level[];
  heap: {
    main: string;
    variants: string[];
    stats: WordStats[];
  };
}

export type RebuildResponse = Level[];

export interface WordStats {
  word: string;
  usage: number;
}

const LevelService = {
  deleteLevel: async (id: number): Promise<boolean> => {
    const req = new Request(DeleteLevelUrl(id), {
      method: 'DELETE',
    });

    return await boolHttp(req);
  },

  getLevels: async (lang: string): Promise<Level[]> => {
    const req = new Request(GetLevelsUrl(lang), {
      method: 'GET',
    });

    return await http<Level[]>(req);
  },

  getLevel: async (id: number): Promise<Level | null> => {
    const req = new Request(GetLevelUrl(id), {
      method: 'GET',
    });

    return await objectHttp(req);
  },

  getDifficultyPoints: async (lang: string): Promise<LevelDifficultyPoint[]> => {
    const req = new Request(GetDifficultyPointsUrl(lang), {
      method: 'GET',
    });

    return await http<LevelDifficultyPoint[]>(req);
  },

  recalculateDifficulty: async (lang: string): Promise<LevelDifficultyPoint[]> => {
    const req = new Request(RecalculateDifficultyUrl(lang), {
      method: 'POST',
    });

    return await http<LevelDifficultyPoint[]>(req);
  },

  reorderLevelsByDifficulty: async (lang: string): Promise<LevelDifficultyPoint[]> => {
    const req = new Request(ReorderLevelsByDifficultyUrl(lang), {
      method: 'POST',
    });

    return await http<LevelDifficultyPoint[]>(req);
  },

  getFullLevel: async (
    lang: string,
    keyword: string = '',
    number: number = 1
  ): Promise<GetFullLevelResponse> => {
    const req = new Request(FullLevelUrl(), {
      method: 'POST',
      body: JSON.stringify({
        lang: lang,
        keyword: keyword,
        number: number,
      } as GetFullLevelRequest),
    });

    return await http<GetFullLevelResponse>(req);
  },

  rebuildLevel: async (lvl: Level): Promise<RebuildResponse> => {
    const req = new Request(RebuildLevelUrl(), {
      method: 'POST',
      body: JSON.stringify(lvl),
    });

    return await http<RebuildResponse>(req);
  },

  saveLevel: async (level: Level): Promise<boolean> => {
    const req = new Request(SaveLevelUrl(), {
      method: level.id === 0 ? 'PUT' : 'POST',
      body: JSON.stringify(level),
    });

    return await boolHttp(req);
  },
};

export default LevelService;
